import { useEffect, useState } from 'react';
import { t } from 'i18next';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth } from '@/contexts/auth';
import { useGetLocationLicenses } from '@/domain/licenses';
import { useGetRegionDailyLiceReport, useGetRegionRangeLiceReport } from '@/domain/liceReport';
import {
    hasRegionDailyReportContent,
    hasRegionRangeReportContent,
    isDailyLiceReportBodyLoading,
    isLiceReportRangeMaxDaysExceededError,
    MAX_LICE_REPORT_RANGE_DAYS,
    MissingPenNotice,
    useDailyLiceReportFeatureEnabled,
    useLiceReportRangeChange,
} from '@/routes/Client/LiceReport';
import { showLiceReportRangeExceededToast } from '@/routes/Client/LiceReport/helpers/rangeExceededToast';
import { getIsoWeek, getIsoWeekYear, subtractTime, toDateString } from '@/utils/date';

import RegionLiceReportPageContent from './RegionLiceReportPageContent';
import { useRegionLiceReportWeeks } from './useRegionLiceReportWeeks';

interface RegionLiceReportProps {
    clientId: number;
}

function RegionLiceReportContainer({ clientId }: RegionLiceReportProps) {
    const { clients, user } = useAuth();
    const client = clients.find((c) => c.id === clientId);

    const isDailyLiceReportEnabled = useDailyLiceReportFeatureEnabled();
    const [mode, setMode] = useState<'weekly' | 'daily'>('weekly');
    const [dailySubMode, setDailySubMode] = useState<'single' | 'range'>('single');
    const [dataSourceType, setDataSourceType] = useState<'reporting' | 'raw'>('reporting');
    const [selectedDate, setSelectedDate] = useState<string>(toDateString(new Date()));
    const [range, setRange] = useState<{ from: string; to: string }>({
        from: toDateString(subtractTime(new Date(), 6, 'day')),
        to: toDateString(new Date()),
    });

    const {
        isLoadingWeekly,
        weeklyError,
        weeklyQuery,
        reports: weeklyReportsData,
        weeklyReports,
        hasWeeks,
        handleWeeklySelectionChange,
        defaultWeekKey,
    } = useRegionLiceReportWeeks(clientId);

    const dailyQuery = useGetRegionDailyLiceReport(clientId, selectedDate, {
        enabled: isDailyLiceReportEnabled && mode === 'daily' && dailySubMode === 'single',
    });
    const {
        isLoading: isLoadingDaily,
        isFetching: isFetchingDaily,
        data: dailyData,
        error: singleDayError,
    } = dailyQuery;

    const rangeQuery = useGetRegionRangeLiceReport(clientId, range.from, range.to, {
        enabled: isDailyLiceReportEnabled && mode === 'daily' && dailySubMode === 'range',
    });
    const {
        isLoading: isLoadingRange,
        isFetching: isFetchingRange,
        data: rangeData,
        error: rangeError,
    } = rangeQuery;

    const licensesQuery = useGetLocationLicenses(clientId);
    const {
        isLoading: isloadingLocationLicense,
        data: locationLicenses,
        error: errorLocationLicense,
    } = licensesQuery;

    // Shown once the weekly reports are in, as the toast it replaced was.
    const showMissingPenNotice = !isLoadingWeekly && hasWeeks;

    useEffect(() => {
        if (
            dailySubMode !== 'range' ||
            !rangeError ||
            !isLiceReportRangeMaxDaysExceededError(rangeError)
        ) {
            return;
        }
        showLiceReportRangeExceededToast(
            t('common.rangeExceedsMaxDays', {
                ns: 'liceReport',
                maxDays: MAX_LICE_REPORT_RANGE_DAYS,
            })
        );
    }, [dailySubMode, rangeError]);

    const handleRangeChange = useLiceReportRangeChange(setRange);

    const reportMode: 'weekly' | 'daily' = isDailyLiceReportEnabled ? mode : 'weekly';

    useEffect(() => {
        if (!isDailyLiceReportEnabled && mode === 'daily') {
            setMode('weekly');
        }
    }, [isDailyLiceReportEnabled, mode]);

    const dailyIsFetching = dailySubMode === 'single' ? isFetchingDaily : isFetchingRange;
    const dailyIsLoading = dailySubMode === 'single' ? isLoadingDaily : isLoadingRange;
    const dailyHasData =
        dailySubMode === 'single'
            ? dailyData != null && hasRegionDailyReportContent(dailyData)
            : rangeData != null && hasRegionRangeReportContent(rangeData);
    const selectedDailyError = (() => {
        if (dailySubMode === 'single') {
            return singleDayError ?? null;
        }
        if (!rangeError || isLiceReportRangeMaxDaysExceededError(rangeError) || dailyHasData) {
            return null;
        }
        return rangeError;
    })();
    const isLoading = isloadingLocationLicense || (reportMode === 'weekly' && isLoadingWeekly);
    const error = reportMode === 'weekly' ? weeklyError : selectedDailyError;
    /*
     * No early return for loading — see the location container. The page renders a skeleton
     * where the table goes, so the chrome around it can stay put.
     */
    if (error || errorLocationLicense) {
        // Only the report on screen is retried; a disabled query would fetch on refetch().
        const reportQuery =
            reportMode === 'weekly'
                ? weeklyQuery
                : dailySubMode === 'single'
                  ? dailyQuery
                  : rangeQuery;
        return (
            <SomethingHappened
                headingLevel="h1"
                {...retryFailedQueries(reportQuery, licensesQuery)}
            />
        );
    }

    const reports = (() => {
        if (reportMode === 'weekly') {
            return weeklyReportsData;
        }
        if (dailySubMode === 'range') {
            if (!rangeData || !hasRegionRangeReportContent(rangeData)) {
                return [];
            }
            return [
                {
                    isoWeek: getIsoWeek(rangeData.to),
                    isoYear: getIsoWeekYear(rangeData.to),
                    interimReport: false,
                    from: rangeData.from,
                    to: rangeData.to,
                    locations: rangeData.locations,
                },
            ];
        }
        if (!dailyData || !hasRegionDailyReportContent(dailyData)) {
            return [];
        }
        return [
            {
                isoWeek: getIsoWeek(dailyData.day),
                isoYear: getIsoWeekYear(dailyData.day),
                interimReport: false,
                day: dailyData.day,
                locations: dailyData.locations,
            },
        ];
    })();

    return (
        <RegionLiceReportPageContent
            clientId={clientId}
            clientName={client?.name ?? ''}
            notice={showMissingPenNotice ? <MissingPenNotice userId={user?.id} /> : null}
            reports={reports}
            weeklyReports={weeklyReports}
            dataLocationLicenses={locationLicenses ?? null}
            mode={reportMode}
            isDailyLiceReportEnabled={isDailyLiceReportEnabled}
            selectedDate={selectedDate}
            dataSourceType={dataSourceType}
            dailySubMode={dailySubMode}
            rangeFrom={range.from}
            rangeTo={range.to}
            isReportLoading={
                reportMode === 'weekly'
                    ? isLoading
                    : isDailyLiceReportBodyLoading(
                          Boolean(dailyIsFetching),
                          dailyIsLoading,
                          dailyHasData
                      )
            }
            onModeChange={setMode}
            onDataSourceChange={setDataSourceType}
            onDateChange={setSelectedDate}
            onDailySubModeChange={setDailySubMode}
            onRangeChange={handleRangeChange}
            onWeeklySelectionChange={handleWeeklySelectionChange}
            defaultWeekKey={defaultWeekKey}
        />
    );
}

export default RegionLiceReportContainer;
export type { RegionLiceReportProps };
export { RegionLiceReportContainer };
